import axios from 'src/utils/axios';
import parse from 'src/utils/parse';
import logger from 'src/utils/logger';
import type {
  WazuhActiveResponseActionLabelOption,
  WazuhActiveResponseActionLabelsQuery,
  WazuhActiveResponseAgentSummaryData,
  WazuhActiveResponseDetail,
  WazuhActiveResponseEndpointItem,
  WazuhActiveResponseItem,
  WazuhActiveResponseQuery,
  WazuhActiveResponseRule,
  WazuhActiveResponseSummaryApiData,
  WazuhActiveResponseSummaryData,
} from 'src/types/mindtime/wazuhActiveResponse';
import type { WazuhDashboardMeta, WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';

const parseFilenameFromContentDisposition = (header: string | undefined): string | null => {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=(?:UTF-8'')?([^;\s]+)/i);
  if (utf8Match) {
    const raw = utf8Match[1].replace(/['"]/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const asciiMatch = header.match(/filename\s*=\s*("?)([^";\n]+)\1/i);
  return asciiMatch ? asciiMatch[2].trim() : null;
};

const readBlobErrorMessage = async (blob: Blob): Promise<string> => {
  try {
    const text = await blob.text();
    try {
      const parsed = JSON.parse(text) as { message?: string };
      return parsed.message || text || 'Export failed';
    } catch {
      return text || 'Export failed';
    }
  } catch {
    return 'Export failed';
  }
};

const parseSuccessRatePercent = (
  value: string | number | null | undefined
): Pick<WazuhActiveResponseSummaryData, 'successRate' | 'successRateLabel'> => {
  if (value == null || value === '') return {};
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return { successRate: value };
  }

  const text = String(value).trim();
  if (!text || text.toUpperCase() === 'N/A') {
    return { successRateLabel: 'N/A' };
  }

  const numeric = Number(text.replace(/%/g, ''));
  if (!Number.isNaN(numeric)) {
    return { successRate: numeric };
  }

  return { successRateLabel: text };
};

const parseActiveResponseSummary = (
  raw: WazuhActiveResponseSummaryApiData | null | undefined,
  meta?: WazuhDashboardMeta
): WazuhActiveResponseSummaryData => ({
  total: raw?.totalResponses,
  successful: raw?.successfulResponses,
  failed: raw?.failedResponses,
  pending: raw?.pendingResponses,
  activeRulesCount: raw?.activeResponseRules,
  ...parseSuccessRatePercent(raw?.successRatePercent),
  ...(meta ? { meta } : {}),
});

const readString = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

const readNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const readStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items = value.map((entry) => readString(entry)).filter(Boolean) as string[];
  return items.length ? items : undefined;
};

const readLogText = (value: unknown): string | string[] | undefined => {
  if (typeof value === 'string') {
    const text = value.trim();
    return text || undefined;
  }
  if (Array.isArray(value)) {
    const lines = value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : readString(entry)))
      .filter(Boolean) as string[];
    return lines.length ? lines : undefined;
  }
  return undefined;
};

const readRule = (raw: unknown): WazuhActiveResponseRule | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const rule = raw as Record<string, unknown>;
  const groups =
    readStringArray(rule.groups) ??
    readStringArray(rule.ruleGroups) ??
    readStringArray(rule.rule_groups);

  const parsed: WazuhActiveResponseRule = {
    description: readString(rule.description ?? rule.ruleDescription ?? rule.rule_description),
    id: readString(rule.id ?? rule.ruleId ?? rule.rule_id) ?? readNumber(rule.id ?? rule.ruleId),
    level: readNumber(rule.level ?? rule.ruleLevel ?? rule.rule_level),
    firedTimes: readNumber(rule.firedTimes ?? rule.fired_times ?? rule.timesFired),
    frequency: readNumber(rule.frequency ?? rule.ruleFrequency ?? rule.rule_frequency),
    groups,
  };

  if (
    !parsed.description &&
    parsed.id == null &&
    parsed.level == null &&
    parsed.firedTimes == null &&
    parsed.frequency == null &&
    !parsed.groups?.length
  ) {
    return undefined;
  }

  return parsed;
};

const parseActiveResponseDetail = (raw: unknown): WazuhActiveResponseDetail | null => {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const id = readString(item.id ?? item._id);
  if (!id) return null;

  const rule = readRule(item.rule ?? item.triggerRule ?? item.trigger_rule);
  const previousAttempts = readLogText(
    item.previousAttempts ?? item.previous_attempts ?? item.previousAttemptsLog
  );
  const eventLogRaw = readLogText(item.eventLog ?? item.event_log ?? item.eventMessage);
  const eventLog =
    typeof eventLogRaw === 'string'
      ? eventLogRaw
      : Array.isArray(eventLogRaw)
        ? eventLogRaw.join('\n')
        : undefined;

  return {
    id,
    _id: readString(item._id),
    timestamp: readString(item.timestamp ?? item.executionTime ?? item.execution_time),
    trigger: readString(item.trigger ?? item.triggerEvent ?? item.trigger_event),
    triggerEvent: readString(item.triggerEvent ?? item.trigger_event ?? item.trigger),
    actionType: readString(item.actionType ?? item.action_type ?? item.actionLabel ?? item.action_label),
    actionLabel: readString(item.actionLabel ?? item.action_label ?? item.actionType ?? item.action_type),
    endpoint: readString(item.endpoint ?? item.agentName ?? item.agent_name),
    organization: readString(item.organization ?? item.client),
    organizationName: readString(item.organizationName ?? item.organization_name ?? item.client),
    status: readString(item.status),
    severity: readString(item.severity ?? item.severityBand ?? item.severity_band),
    durationSec: readNumber(item.durationSec ?? item.duration_sec ?? item.duration),
    duration: readNumber(item.duration ?? item.durationSec),
    wazuhAgentId: readString(item.wazuhAgentId ?? item.wazuh_agent_id),
    systemWazuhAgentId: readString(item.systemWazuhAgentId ?? item.system_wazuh_agent_id),
    program: readString(item.program ?? item.activeResponseProgram ?? item.active_response_program),
    sourceIp: readString(item.sourceIp ?? item.source_ip ?? item.srcIp ?? item.src_ip),
    sourceUser: readString(item.sourceUser ?? item.source_user ?? item.srcUser ?? item.src_user),
    sourcePort: readString(item.sourcePort ?? item.source_port ?? item.srcPort ?? item.src_port),
    executionTime: readString(item.executionTime ?? item.execution_time ?? item.timestamp),
    hostname: readString(item.hostname),
    hostName: readString(item.hostName ?? item.host_name),
    agentIp: readString(item.agentIp ?? item.agent_ip ?? item.ip),
    resellerName: readString(item.resellerName ?? item.reseller_name ?? item.reseller),
    previousAttempts,
    eventLog,
    rule,
    mitreId: readString(item.mitreId ?? item.mitre_id),
    mitreIds: readStringArray(item.mitreIds ?? item.mitre_ids),
    tactics: readStringArray(item.tactics ?? item.mitreTactics ?? item.mitre_tactics),
    techniqueNames: readStringArray(
      item.techniqueNames ?? item.technique_names ?? item.mitreTechniques
    ),
    result: readString(item.result ?? item.dispatchResult ?? item.dispatch_result),
    decoder: readString(item.decoder),
    logLocation: readString(item.logLocation ?? item.log_location ?? item.logPath ?? item.log_path),
  };
};

const parseActionLabelOptions = (data: unknown): WazuhActiveResponseActionLabelOption[] => {
  const raw = Array.isArray(data)
    ? data
    : (data as { labels?: unknown; actionLabels?: unknown } | null | undefined)?.labels ??
      (data as { actionLabels?: unknown } | null | undefined)?.actionLabels;

  if (!Array.isArray(raw)) return [];

  return raw
    .map((item): WazuhActiveResponseActionLabelOption | null => {
      if (typeof item === 'string') {
        const text = item.trim();
        return text ? { label: text, value: text } : null;
      }
      if (item && typeof item === 'object') {
        const entry = item as { label?: string; value?: string; actionLabel?: string };
        const value = (entry.value ?? entry.actionLabel ?? entry.label ?? '').trim();
        const label = (entry.label ?? entry.value ?? entry.actionLabel ?? '').trim();
        return value ? { label: label || value, value } : null;
      }
      return null;
    })
    .filter((option): option is WazuhActiveResponseActionLabelOption => Boolean(option));
};

class WazuhActiveResponseApi {
  private appendQuery(url: string, params?: Record<string, unknown>): string {
    if (!params) return url;
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });
    const query = qs.toString();
    if (!query) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${query}`;
  }

  async getAgentSummary(
    systemWazuhAgentId: string
  ): Promise<WazuhActiveResponseAgentSummaryData> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      const id = systemWazuhAgentId.trim();
      axios
        .get(`${baseUrl}/api/v1/active-responses/agent-summary/${encodeURIComponent(id)}`)
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const body = parsedResponse?.data;
            const data = body?.data as WazuhActiveResponseSummaryApiData | undefined;
            if (!data || typeof data !== 'object') {
              throw new Error('Invalid response: missing agent active-responses summary');
            }
            resolve({
              ...parseActiveResponseSummary(data, body?.meta),
              systemWazuhAgentId: id,
            });
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getActionLabels(
    params?: WazuhActiveResponseActionLabelsQuery
  ): Promise<WazuhActiveResponseActionLabelOption[]> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/active-responses/action-labels`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            resolve(parseActionLabelOptions(data));
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getSummary(
    params?: WazuhScopeParams & Pick<WazuhActiveResponseQuery, 'range'>
  ): Promise<WazuhActiveResponseSummaryData> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/active-responses/summary`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const body = parsedResponse?.data;
            const data = body?.data as WazuhActiveResponseSummaryApiData | undefined;
            if (!data || typeof data !== 'object') {
              throw new Error('Invalid response: missing active-responses summary');
            }
            resolve(parseActiveResponseSummary(data, body?.meta));
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getResponses(
    params: WazuhActiveResponseQuery
  ): Promise<WazuhPagedData<WazuhActiveResponseItem>> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/active-responses`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            if (!data || typeof data !== 'object') {
              throw new Error('Invalid response: active-responses must contain data object');
            }
            resolve({
              page: data.page ?? 0,
              pageSize: data.pageSize ?? 0,
              totalRecords: data.totalRecords ?? 0,
              totalPages: data.totalPages ?? 0,
              items: Array.isArray(data.items) ? data.items : [],
            });
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async listEndpoints(
    params: WazuhActiveResponseQuery
  ): Promise<WazuhPagedData<WazuhActiveResponseEndpointItem>> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/active-responses/endpoints`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            if (!data || !Array.isArray(data.items)) {
              throw new Error('Invalid response: active-responses/endpoints must contain items array');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getResponse(id: string): Promise<WazuhActiveResponseDetail | null> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      const recordId = id.trim();
      if (!recordId) {
        resolve(null);
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/active-responses/${encodeURIComponent(recordId)}`)
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            resolve(parseActiveResponseDetail(data));
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async export(
    params: WazuhActiveResponseQuery
  ): Promise<{ blob: Blob; filename: string }> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/active-responses/export`, params), {
          responseType: 'blob',
          headers: { Accept: '*/*' },
        })
        .then(async (response) => {
          try {
            const blob = response.data as Blob;
            if (!blob || response.status < 200 || response.status >= 300) {
              throw new Error(await readBlobErrorMessage(blob));
            }

            const contentType = String(response.headers['content-type'] || '');
            if (contentType.includes('application/json')) {
              throw new Error(await readBlobErrorMessage(blob));
            }

            const filename =
              parseFilenameFromContentDisposition(
                response.headers['content-disposition'] as string | undefined
              ) || 'active_responses_export.csv';

            resolve({ blob, filename });
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch(async (error) => {
          if (error?.response?.data instanceof Blob) {
            try {
              reject(new Error(await readBlobErrorMessage(error.response.data)));
              return;
            } catch {
              // fall through
            }
          }
          logger(error, 'error');
          reject(error instanceof Error ? error : new Error('Export failed'));
        });
    });
  }
}

export const mindtimeWazuhActiveResponseApi = new WazuhActiveResponseApi();
